"use client"

import * as React from "react"
import { cn } from "@sarkiai/utils"
import { Sidebar, MobileSidebar, type NavigationItem } from "../navigation/sidebar"
import { Header } from "../navigation/header"

interface DashboardLayoutProps {
  children: React.ReactNode
  navigation: NavigationItem[]
  branding?: {
    logo?: React.ReactNode
    title?: string
    homeUrl?: string
  }
  user?: {
    name?: string | null
    email?: string | null
    image?: string | null
  }
  onSignOut?: () => void
  sidebarFooter?: React.ReactNode
  headerActions?: React.ReactNode
  defaultCollapsed?: boolean
}

export function DashboardLayout({
  children,
  navigation,
  branding,
  user,
  onSignOut,
  sidebarFooter,
  headerActions,
  defaultCollapsed = true,
}: DashboardLayoutProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(defaultCollapsed)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)

  const handleToggleSidebar = React.useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev)
  }, [])

  const handleMobileMenuToggle = React.useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev)
  }, [])

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Desktop Sidebar */}
      <div className="hidden md:block fixed left-0 top-0 bottom-0 z-40">
        <Sidebar
          navigation={navigation}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          branding={branding}
          user={user}
          onSignOut={onSignOut}
        />
      </div>

      {/* Mobile Sidebar */}
      <MobileSidebar
        navigation={navigation}
        branding={branding}
        user={user}
        onSignOut={onSignOut}
        open={isMobileMenuOpen}
        onOpenChange={setIsMobileMenuOpen}
      />

      {/* Main Content Area */}
      <div
        className={cn(
          "flex flex-col flex-1 transition-all duration-300 min-w-0",
          isSidebarCollapsed ? "md:pl-14" : "md:pl-56",
        )}
      >
        <Header
          title={branding?.title}
          onMenuClick={handleMobileMenuToggle}
          showMenuButton={true}
          user={user}
          onSignOut={onSignOut}
          actions={headerActions}
        />

        <main className="flex-1 relative">
          {children}
        </main>
      </div>
    </div>
  )
}

export type { NavigationItem }
